import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { Search, Loader2, ChevronLeft, ChevronRight, X, Plus, Archive, MessageSquare, Phone, Calendar as CalendarIcon, Filter, Mail, User, Globe, UserSearch, ChevronDown, BookOpen, Download, FileText, FileImage, FileVideo, File } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { withAppTimeZone } from '@/lib/timezone';
import { Badge } from '@/components/ui/badge';
import { DatePicker } from '@/components/ui/date-picker';
import { searchArchivedMessages, downloadArchivedMedia } from '@/lib/api/archived-messages';
import { ConversationContextView } from '@/components/messages/conversation-context-view';
import { searchPatients } from '@/lib/api/users';
import { cn } from '@/lib/utils';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { PatientSearchDropdown } from '@/components/messages/patient-search-dropdown';
import { useHasPermission } from '@/hooks/use-permissions';

const PAGE_SIZE = 20;

function formatTimestamp(timestamp, messageDateStr) {
  const raw = timestamp || messageDateStr;
  if (!raw) return '';
  try {
    const ms = typeof raw === 'number' ? raw * 1000 : new Date(raw).getTime();
    if (Number.isNaN(ms)) return '';
    return new Date(ms).toLocaleString(undefined, withAppTimeZone({
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    }));
  } catch { return ''; }
}

function toRFC3339(date) {
  if (!date) return undefined;
  return date.toISOString();
}

function formatDOB(dobString) {
  if (!dobString) return 'Not provided';
  try {
    const d = new Date(dobString);
    if (Number.isNaN(d.getTime())) return dobString;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return dobString;
  }
}

/** Returns a lucide icon component matching the MIME type. */
function mediaIcon(mime) {
  if (!mime) return File;
  if (mime.startsWith('image/')) return FileImage;
  if (mime.startsWith('video/')) return FileVideo;
  if (mime.includes('pdf') || mime.includes('document') || mime.includes('word') || mime.includes('text')) return FileText;
  return File;
}

/**
 * A compact download button rendered inside a message bubble when the message
 * has a downloadable media_url. Shows an animated spinner while downloading
 * and an error label on failure.
 */
function MediaDownloadButton({ message, canExport = false }) {
  const [downloading, setDownloading] = useState(false);
  const [dlError, setDlError] = useState('');

  const mediaUrl = String(message.media_url || '').trim();
  const mime = String(message.mime || '').toLowerCase();
  const caption = String(message.caption || '').trim();

  // Derive a human-readable label from the URL
  const rawName = decodeURIComponent(mediaUrl.split('/').pop() || 'file');
  const label = caption || rawName;

  // Pick icon
  const IconComp = mediaIcon(mime);

  const handleDownload = async (e) => {
    e.stopPropagation();
    if (downloading) return;
    setDownloading(true);
    setDlError('');
    try {
      await downloadArchivedMedia({ mediaUrl, fileName: rawName });
    } catch (err) {
      setDlError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const body = (
    <>
      {/* File icon */}
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-500/15 text-amber-600 transition-colors group-hover/dl:bg-amber-500/25 dark:bg-amber-500/20 dark:text-amber-400">
        {downloading
          ? <Loader2 className="h-4 w-4 animate-spin" />
          : <IconComp className="h-4 w-4" />}
      </div>
      {/* Name + mime */}
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-semibold text-foreground">{label}</p>
        {mime && (
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{mime.split('/').pop()}</p>
        )}
      </div>
    </>
  );

  // archive:view still entitles the reader to see that a file exists, so the chip stays —
  // just inert, with no click target and no download glyph.
  if (!canExport) {
    return (
      <div className="mt-1 flex flex-col gap-1 mr-5">
        <div
          className="flex items-center gap-2.5 rounded-xl border border-amber-400/25 bg-amber-500/5 px-3 py-2.5 text-start opacity-70 dark:border-amber-400/15 dark:bg-amber-500/8"
          title="Downloading archived media requires Archive → Manage"
        >
          {body}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-1 flex flex-col gap-1 mr-5">
      <button
        type="button"
        onClick={handleDownload}
        disabled={downloading}
        className="group/dl flex items-center gap-2.5 rounded-xl border border-amber-400/40 bg-amber-500/8 px-3 py-2.5 text-start transition-all hover:border-amber-400/70 hover:bg-amber-500/15 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amber-400/25 dark:bg-amber-500/10 dark:hover:border-amber-400/50 dark:hover:bg-amber-500/20"
      >
        {body}
        {/* Download icon */}
        <Download className="h-3.5 w-3.5 shrink-0 text-amber-500 opacity-70 transition-opacity group-hover/dl:opacity-100" />
      </button>
      {dlError && (
        <p className="text-[10px] text-destructive">{dlError}</p>
      )}
    </div>
  );
}

function ResultBubble({ message, phrases, onOpenContext, canExport }) {
  const fromMe = Boolean(message.from_me);
  const text = String(message.data || message.caption || '').trim();
  const time = formatTimestamp(message.timestamp, message.message_date);
  const jidAuthor = message.chat_jid ? String(message.chat_jid).split('@')[0] : '';
  const sender = String(message.sender || jidAuthor || message.phone || '').trim();
  const chatName = String(message.chat_name || message.patient?.displayName || '').trim();
  const hasMedia = Boolean(message.media);
  const isSticker = Boolean(message.sticker);
  const mime = String(message.mime || '').toLowerCase();
  const isImage = hasMedia && (mime.startsWith('image/') || Boolean(message.thumb));

  const highlightText = (str) => {
    if (!str || !phrases || phrases.length === 0) return str;
    const validPhrases = phrases.filter(Boolean);
    if (validPhrases.length === 0) return str;
    const escaped = validPhrases.map(p => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const regex = new RegExp(`(${escaped.join('|')})`, 'gi');
    const parts = str.split(regex);
    return parts.map((part, i) =>
      regex.test(part)
        ? <mark key={i} className="rounded-sm bg-amber-300/60 px-0.5 text-foreground dark:bg-amber-500/40">{part}</mark>
        : part
    );
  };

  return (
    <div className={cn('group flex items-end gap-2', fromMe ? 'justify-end' : 'justify-start')}>
      {!fromMe && (
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-[10px] font-semibold text-amber-700 ring-1 ring-amber-400/30 dark:bg-amber-500/20 dark:text-amber-300 dark:ring-amber-400/25">
          {chatName.split(/\s+/).slice(0, 2).map(p => p[0]?.toUpperCase() || '').join('')}
        </span>
      )}
      <article className={cn(
        'relative min-w-0 rounded-2xl border px-4 py-3 text-start transition-shadow hover:shadow-md',
        fromMe
          ? 'border-amber-400/20 bg-amber-500/8 max-w-[85%] dark:border-amber-400/25 dark:bg-amber-500/12'
          : 'border-amber-400/30 bg-amber-50/60 shadow-sm max-w-[85%] dark:border-amber-400/20 dark:bg-amber-950/20 dark:shadow-none',
      )}>
        {isSticker && <p className="text-xs italic text-muted-foreground">[Sticker]</p>}
        {isImage && !text && <p className="text-xs italic text-muted-foreground">[Image]</p>}
        {/* Media download button — shown when media_url is present and it's not a plain image/sticker */}
        {hasMedia && !isImage && !isSticker && message.media_url && (
          <MediaDownloadButton message={message} canExport={canExport} />
        )}
        {/* Fallback for media without a URL */}
        {hasMedia && !isImage && !isSticker && !message.media_url && !text && (
          <p className="text-xs italic text-muted-foreground">[{mime || 'Media'}]</p>
        )}
        {!hasMedia && text && (
          <p className="wrap-break-word mr-6 text-sm leading-relaxed text-foreground">
            {highlightText(text)}
          </p>
        )}
        <div className={cn('mt-1 whitespace-nowrap text-[11px] text-muted-foreground', fromMe ? 'text-right' : 'text-left')}>
          {time}
        </div>
        {/* Context button – visible on hover */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onOpenContext?.(message); }}
          title="Open conversation context"
          className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-amber-500/10 text-amber-600 opacity-0 transition-all hover:bg-amber-500/25 hover:text-amber-700 group-hover:opacity-100 dark:bg-amber-500/15 dark:text-amber-400 dark:hover:bg-amber-500/30 dark:hover:text-amber-300"
        >
          <BookOpen className="h-3 w-3" />
        </button>
      </article>
      {fromMe && (
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-[10px] font-semibold text-amber-700 ring-1 ring-amber-400/30 dark:bg-amber-500/20 dark:text-amber-300 dark:ring-amber-400/25">
          {message.phone.split(/\s+/).slice(0, 2).map(p => p[0]?.toUpperCase() || '').join('')}
        </span>
      )}
    </div>
  );
}

function JidGroup({ jid, messages, phrases, onShowPatientDetails, onOpenContext, canExport }) {
  const [isOpen, setIsOpen] = useState(true);
  const chatName = String(messages[0].chat_name || messages[0].patient_phone || '').trim();
  const patient = messages.find(m => m.patient)?.patient || null;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="rounded-lg border border-border/50 bg-background/50">
      <CollapsibleTrigger className="flex w-full items-center justify-between px-3 py-2 hover:bg-muted/50 rounded-lg transition-colors">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-3.5 w-3.5 text-amber-500/70" />
          <span className="text-sm font-medium">{chatName}</span>
          {patient && patient.displayName && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onShowPatientDetails(patient);
              }}
              className="inline-flex items-center gap-1 rounded bg-amber-500/10 px-2 py-0.5 text-[11px] font-semibold text-amber-600 hover:bg-amber-500/20 hover:text-amber-800 dark:bg-amber-500/20 dark:text-amber-400 dark:hover:bg-amber-500/30 dark:hover:text-amber-300 underline underline-offset-2 transition-colors cursor-default"
            >
              <User className="h-3 w-3 shrink-0" />
              {patient.displayName}
            </button>
          )}
          <span className="text-[10px] text-muted-foreground ml-2">{messages.length} msg{messages.length !== 1 ? 's' : ''}</span>
        </div>
        <ChevronRight className={cn("h-3.5 w-3.5 text-muted-foreground transition-transform", isOpen && "rotate-90")} />
      </CollapsibleTrigger>
      <CollapsibleContent className="px-3 pb-3 space-y-3 border-t border-border/50 mt-1 pt-3">
        {messages.map((msg, i) => (
          <ResultBubble
            key={msg._id || msg.key_id || msg.message_id || i}
            message={msg}
            phrases={phrases}
            onOpenContext={onOpenContext}
            canExport={canExport}
          />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

function PhoneGroup({ phone, jidGroups, patient, phrases, onShowPatientDetails, onOpenContext, canExport }) {
  const [isOpen, setIsOpen] = useState(true);
  const count = Object.values(jidGroups).reduce((sum, msgs) => sum + msgs.length, 0);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="rounded-xl border border-border bg-card shadow-sm">
      <div
        className="flex w-full items-center justify-between px-4 py-3 hover:bg-muted/50 rounded-xl transition-colors cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
            <span className="font-semibold text-sm">{phone}</span>
          </div>

          <Badge variant="secondary" className="text-[10px] shrink-0">{count} message{count !== 1 ? 's' : ''}</Badge>
        </div>
        <ChevronRight className={cn("h-4 w-4 text-muted-foreground transition-transform shrink-0", isOpen && "rotate-90")} />
      </div>
      <CollapsibleContent className="px-4 pb-4 space-y-3 border-t border-border mt-1 pt-3">
        {Object.entries(jidGroups).map(([jid, msgs]) => (
          <JidGroup key={jid} jid={jid} messages={msgs} phrases={phrases} onShowPatientDetails={onShowPatientDetails} onOpenContext={onOpenContext} canExport={canExport} />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

function SearchPage() {
  // archive:manage — read ONCE here rather than inside MediaDownloadButton: that component
  // renders per media hit, and a store subscription in each would attach one window listener
  // per row. Threaded down through PhoneGroup → JidGroup → ResultBubble.
  const canExport = useHasPermission('archive', 'manage');
  const [phone, setPhone] = useState('');
  const [phraseInput, setPhraseInput] = useState('');
  const [phrases, setPhrases] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const [selectedPatientForPhone, setSelectedPatientForPhone] = useState(null);

  const handleShowPatientDetails = useCallback((patient) => {
    setSelectedPatient(patient);
  }, []);

  const handlePatientSelected = useCallback((patient) => {
    const phones = patient.contacts?.Phones || [];
    const chosenPhone = patient._selectedPhone
      || (phones.find(p => p.Verified) || phones[0])?.E164
      || '';
    setPhone(chosenPhone);
    setSelectedPatientForPhone({ ...patient, _resolvedPhone: chosenPhone });
  }, []);

  const clearSelectedPatient = useCallback(() => {
    setSelectedPatientForPhone(null);
    setPhone('');
  }, []);

  const [messages, setMessages] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentSkip, setCurrentSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  // Context view state: null = show search results, object = show conversation context
  const [contextView, setContextView] = useState(null); // { phone, anchorMessageId, anchorMessage }

  const handleOpenContext = useCallback((message) => {
    console.log(message);
    const msgPhone = String(message.patient_phone || message.sender).trim();
    const msgId = message.message_id;
    setContextView({ phone: msgPhone, anchorMessageId: msgId, anchorMessage: message });
  }, [phone]);

  const handleCloseContext = useCallback(() => {
    setContextView(null);
  }, []);

  const resultsRef = useRef(null);

  const patientsForMessages = useMemo(() => {
    return messages.reduce((acc, msg) => {
      if (msg.patient) {
        acc[msg._id] = msg.patient;
      }
      return acc;
    }, {});
  }, [messages]);

  const groupedMessages = useMemo(() => {
    return messages.reduce((acc, msg) => {
      const p = msg.phone || 'Unknown Phone';
      if (!acc[p]) {
        acc[p] = {
          jidGroups: {},
          //patient: null,
        };
      }

      // if (msg.patient && !acc[p].patient) {
      //   acc[p].patient = msg.patient;
      // }

      //const rawJid = msg.chat_name || msg.chat_jid || '';
      //const jidAuthor = rawJid.split('@')[0] || 'Unknown Author';
      if (!acc[p].jidGroups[msg.chat_jid]) {
        acc[p].jidGroups[msg.chat_jid] = [];
      }

      acc[p].jidGroups[msg.chat_jid].push(msg);
      return acc;
    }, {});
  }, [messages]);

  const addPhrase = useCallback(() => {
    const trimmed = phraseInput.trim();
    if (trimmed && !phrases.includes(trimmed)) {
      setPhrases(prev => [...prev, trimmed]);
    }
    setPhraseInput('');
  }, [phraseInput, phrases]);

  const removePhrase = useCallback((phrase) => {
    setPhrases(prev => prev.filter(p => p !== phrase));
  }, []);

  const hasFilters = phone.trim() || phrases.length > 0 || fromDate || toDate;

  const doSearch = useCallback(async (skip = 0, overridePhrases) => {
    const effectivePhrases = overridePhrases ?? phrases;
    const effectiveHasFilters = phone.trim() || effectivePhrases.length > 0 || fromDate || toDate;
    if (!effectiveHasFilters) return;
    setLoading(true);
    setError('');
    try {
      const result = await searchArchivedMessages({
        phone: phone.trim() || undefined,
        phrases: effectivePhrases.length > 0 ? effectivePhrases : undefined,
        fromDate: toRFC3339(fromDate),
        toDate: toRFC3339(toDate),
        limit: PAGE_SIZE,
        skip,
      });
      setMessages(result.messages);
      setTotalCount(result.count);
      setCurrentSkip(skip);
      setHasSearched(true);
      resultsRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Search failed');
      setMessages([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [phone, phrases, fromDate, toDate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (phraseInput.trim()) {
      const trimmed = phraseInput.trim();
      const nextPhrases = phrases.includes(trimmed) ? phrases : [...phrases, trimmed];
      setPhrases(nextPhrases);
      setPhraseInput('');
      doSearch(0, nextPhrases);
      return;
    }
    doSearch(0);
  };

  const clearAll = () => {
    setPhone('');
    setPhraseInput('');
    setPhrases([]);
    setFromDate(null);
    setToDate(null);
    setMessages([]);
    setTotalCount(0);
    setCurrentSkip(0);
    setError('');
    setHasSearched(false);
    setSelectedPatientForPhone(null);
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const currentPage = Math.floor(currentSkip / PAGE_SIZE) + 1;

  return (
    <div className="flex h-full flex-col gap-0 overflow-hidden lg:flex-row">
      {/* ── Search Panel ── */}
      <div className="flex w-full flex-col border-b border-border lg:w-[380px] lg:min-w-[340px] lg:max-w-[440px] lg:border-b-0 lg:border-r">
        <div className="flex items-center gap-2.5 border-b border-border px-5 py-4">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 shadow-sm">
            <Search className="h-4 w-4 text-white" />
          </div>
          <div>
            <h1 className="text-base font-semibold tracking-tight">Search Archive</h1>
            <p className="text-xs text-muted-foreground">Find archived messages</p>
          </div>
        </div>

        <ScrollArea className="min-h-0 flex-1">
          <form onSubmit={handleSubmit} className="flex flex-col gap-4 p-5">
          {/* Patient search – hidden once a patient is selected */}
          {!selectedPatientForPhone && (
            <>
              <PatientSearchDropdown onSelectPatient={handlePatientSelected} />
              <div className="h-px bg-border" />
            </>
          )}

          {/* Phone */}
          <div className="space-y-1.5">

            {/* Selected patient card */}
            {selectedPatientForPhone && (() => {
              const sp = selectedPatientForPhone;
              const name = sp.displayName
                || `${sp.profile?.FirstName || ''} ${sp.profile?.LastName || ''}`.trim()
                || 'Patient';
              const allPhones = sp.contacts?.Phones || [];
              const dob = sp.profile?.DOB ? sp.profile.DOB.slice(0, 10) : null;
              const sex = sp.profile?.Sex || null;
              const initials =
                (sp.profile?.FirstName?.[0] || sp.displayName?.[0] || 'P').toUpperCase() +
                (sp.profile?.LastName?.[0] || '').toUpperCase();
              return (
                <div className="rounded-xl border border-amber-400/40 bg-amber-500/5 dark:border-amber-400/25 dark:bg-amber-500/8 overflow-hidden">
                  {/* Card header */}
                  <div className="flex items-center gap-3 px-3 py-3 border-b border-amber-400/20">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-sm font-bold text-white shadow-sm">
                      {initials}
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-sm font-semibold text-foreground">{name}</span>
                      {sp.status && (
                        <span className={cn(
                          'text-[10px] font-semibold uppercase',
                          sp.status === 'active' ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'
                        )}>{sp.status}</span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={clearSelectedPatient}
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
                      title="Remove selected patient"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  {/* Card body */}
                  <div className="px-3 py-2.5 space-y-2 text-xs">
                    {/* DOB / Sex */}
                    {(dob || sex) && (
                      <div className="flex items-center gap-4 text-muted-foreground">
                        {dob && (
                          <span className="flex items-center gap-1">
                            <CalendarIcon className="h-3 w-3" />
                            <span className="font-medium text-foreground">DOB:</span> {dob}
                          </span>
                        )}
                        {sex && (
                          <span>
                            <span className="font-medium text-foreground">Sex:</span> {sex}
                          </span>
                        )}
                      </div>
                    )}
                    {/* Phones */}
                    {allPhones.length > 0 && (
                      <div className="space-y-1">
                        <span className="flex items-center gap-1 text-[10px] font-semibold uppercase text-muted-foreground">
                          <Phone className="h-2.5 w-2.5" /> Phones
                        </span>
                        {allPhones.map((p, i) => (
                          <div key={i} className="flex items-center justify-between">
                            <span className="font-mono text-foreground">{p.E164}</span>
                            {p.Verified && (
                              <Badge className="border-none bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300 text-[9px]">Verified</Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* <label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <Phone className="h-3 w-3" /> Phone number
            </label>

            <Input
              id="search-phone"
              placeholder="e.g. 6512345678"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              readOnly={!!selectedPatientForPhone}
              className={selectedPatientForPhone ? 'bg-muted/50 cursor-not-allowed text-muted-foreground' : ''}
            /> */}
          </div>

          {/* Phrases */}
          <div className="space-y-1.5">
            <label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <MessageSquare className="h-3 w-3" /> Search phrases
            </label>
            <div className="flex gap-1.5">
              <Input
                id="search-phrase-input"
                placeholder="Type a phrase…"
                value={phraseInput}
                onChange={(e) => setPhraseInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && phraseInput.trim() && !e.shiftKey) {
                    e.preventDefault();
                    addPhrase();
                  }
                }}
              />
              <Button type="button" size="icon" variant="outline" onClick={addPhrase} disabled={!phraseInput.trim()}>
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>
            {phrases.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {phrases.map((p) => (
                  <Badge key={p} variant="secondary" className="gap-1 pl-2 pr-1 text-xs">
                    {p}
                    <button type="button" onClick={() => removePhrase(p)} className="ml-0.5 rounded-full p-0.5 hover:bg-foreground/10">
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Date range */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <CalendarIcon className="h-3 w-3" /> From
              </label>
              <DatePicker date={fromDate} setDate={setFromDate} placeholder="Start date" />
            </div>
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <CalendarIcon className="h-3 w-3" /> To
              </label>
              <DatePicker date={toDate} setDate={setToDate} placeholder="End date" />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-1">
            <Button
              id="search-submit"
              type="submit"
              className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-sm hover:from-amber-600 hover:to-orange-600"
              disabled={loading || !hasFilters}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Search
            </Button>
            {hasFilters && (
              <Button type="button" variant="outline" size="sm" onClick={clearAll} className="shrink-0">
                <X className="h-3.5 w-3.5" /> Clear
              </Button>
            )}
          </div>

          {/* Active filters summary */}
          {hasSearched && (
            <div className="flex items-center gap-2 rounded-lg border border-amber-400/30 bg-amber-50/50 px-3 py-2 text-xs text-amber-800 dark:border-amber-400/20 dark:bg-amber-950/30 dark:text-amber-300">
              <Filter className="h-3 w-3 shrink-0" />
              <span className="font-medium">{totalCount.toLocaleString()}</span> result{totalCount !== 1 ? 's' : ''} found
            </div>
          )}
          </form>
        </ScrollArea>
      </div>

      {/* ── Results Panel ── */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {contextView ? (
          <ConversationContextView
            phone={contextView.phone}
            anchorMessageId={contextView.anchorMessageId}
            anchorMessage={contextView.anchorMessage}
            onClose={handleCloseContext}
            canExport={canExport}
          />
        ) : (
          <>
            {/* Results header */}
            <div className="flex items-center justify-between border-b border-border px-5 py-3">
              <div className="flex items-center gap-2">
                <Archive className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                <span className="text-sm font-medium">Results</span>
              </div>
              {hasSearched && (totalCount >= PAGE_SIZE || currentPage > 1) && (
                <div className="flex items-center gap-1.5">
                  <Button
                    variant="outline" size="ssm"
                    disabled={currentPage <= 1 || loading}
                    onClick={() => doSearch(currentSkip - PAGE_SIZE)}
                  >
                    <ChevronLeft className="h-3 w-3" />
                  </Button>
                  <span className="min-w-[4.5rem] text-center text-xs tabular-nums text-muted-foreground">
                    {currentPage}
                  </span>
                  <Button
                    variant="outline" size="ssm"
                    disabled={loading || messages.length < PAGE_SIZE}
                    onClick={() => doSearch(currentSkip + PAGE_SIZE)}
                  >
                    <ChevronRight className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>

            {/* Results body */}
            <ScrollArea viewportRef={resultsRef} className="min-h-0 flex-1">
              {loading && (
                <div className="flex flex-col items-center justify-center gap-3 py-20">
                  <Loader2 className="h-7 w-7 animate-spin text-amber-500" />
                  <p className="text-sm text-muted-foreground">Searching archived messages…</p>
                </div>
              )}

              {error && !loading && (
                <div className="mx-5 mt-5 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                  {error}
                </div>
              )}

              {!loading && !error && hasSearched && messages.length === 0 && (
                <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted">
                    <Search className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <p className="text-sm font-medium text-muted-foreground">No messages found</p>
                  <p className="text-xs text-muted-foreground/70">Try adjusting your search filters</p>
                </div>
              )}

              {!loading && !error && !hasSearched && (
                <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/30 dark:to-orange-900/30">
                    <Archive className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                  </div>
                  <p className="text-sm font-medium text-muted-foreground">Search archived messages</p>
                  <p className="max-w-xs text-xs text-muted-foreground/70">
                    Use at least one filter — phone, phrases, or date range — to search.
                  </p>
                </div>
              )}

              {!loading && messages.length > 0 && (
                <div className="flex flex-col gap-3 p-5">

                  {Object.entries(groupedMessages).map(([p, { jidGroups }]) => (
                    <PhoneGroup
                      key={p}
                      phone={p}
                      jidGroups={jidGroups}
                      patient={patientsForMessages[p]}
                      phrases={phrases}
                      onShowPatientDetails={handleShowPatientDetails}
                      onOpenContext={handleOpenContext}
                      canExport={canExport}
                    />
                  ))}
                </div>
              )}
            </ScrollArea>

            {/* Bottom pagination */}
            {hasSearched && totalCount > PAGE_SIZE && !loading && (
              <div className="flex items-center justify-between border-t border-border px-5 py-2.5">
                <p className="text-xs text-muted-foreground">
                  Showing {currentSkip + 1}–{Math.min(currentSkip + PAGE_SIZE, totalCount)} of {totalCount.toLocaleString()}
                </p>
                <div className="flex items-center gap-1.5">
                  <Button variant="outline" size="ssm" disabled={currentPage <= 1} onClick={() => doSearch(currentSkip - PAGE_SIZE)}>
                    <ChevronLeft className="h-3 w-3" /> Prev
                  </Button>
                  <Button variant="outline" size="ssm" disabled={currentPage >= totalPages} onClick={() => doSearch(currentSkip + PAGE_SIZE)}>
                    Next <ChevronRight className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>{/* end Results Panel */}

      <Dialog open={!!selectedPatient} onOpenChange={(open) => !open && setSelectedPatient(null)}>
        <DialogContent className="max-w-md overflow-hidden rounded-2xl border bg-background p-0 shadow-lg sm:max-w-lg">
          {selectedPatient && (
            <div className="flex flex-col">
              {/* Header with gradient and avatar */}
              <div className="relative bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-8 text-white">
                <div className="flex items-center gap-4">
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-white/20 text-2xl font-bold text-white shadow-inner backdrop-blur-sm ring-2 ring-white/30">
                    {selectedPatient.profile?.FirstName?.[0]?.toUpperCase() || selectedPatient.displayName?.[0]?.toUpperCase() || 'P'}
                    {selectedPatient.profile?.LastName?.[0]?.toUpperCase() || ''}
                  </div>
                  <div className="space-y-1">
                    <DialogTitle className="text-xl font-bold tracking-tight text-white">
                      {selectedPatient.displayName || `${selectedPatient.profile?.FirstName || ''} ${selectedPatient.profile?.LastName || ''}`.trim() || 'Patient Details'}
                    </DialogTitle>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className="bg-white/20 text-white hover:bg-white/30 border-none text-[10px] uppercase font-semibold tracking-wider">
                        {selectedPatient.userType || 'patient'}
                      </Badge>
                      <Badge className={cn(
                        "border-none text-[10px] uppercase font-semibold tracking-wider",
                        selectedPatient.status === 'active' ? "bg-emerald-500/30 text-emerald-100" : "bg-zinc-500/30 text-zinc-100"
                      )}>
                        {selectedPatient.status || 'unknown'}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>

              {/* Patient Details Content */}
              <ScrollArea viewportClassName="max-h-[60vh] p-6">
                <div className="space-y-6">

                {/* Profile section */}
                <div className="space-y-3">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Profile Information</h3>
                  <div className="grid grid-cols-2 gap-4 rounded-xl border bg-muted/30 p-4">
                    <div>
                      <span className="text-[10px] font-medium text-muted-foreground uppercase">First Name</span>
                      <p className="text-sm font-medium text-foreground">{selectedPatient.profile?.FirstName || '—'}</p>
                    </div>
                    <div>
                      <span className="text-[10px] font-medium text-muted-foreground uppercase">Last Name</span>
                      <p className="text-sm font-medium text-foreground">{selectedPatient.profile?.LastName || '—'}</p>
                    </div>
                    {selectedPatient.profile?.MiddleName && (
                      <div className="col-span-2">
                        <span className="text-[10px] font-medium text-muted-foreground uppercase">Middle Name</span>
                        <p className="text-sm font-medium text-foreground">{selectedPatient.profile.MiddleName}</p>
                      </div>
                    )}
                    <div>
                      <span className="flex items-center gap-1 text-[10px] font-medium text-muted-foreground uppercase">
                        <CalendarIcon className="h-3 w-3" /> DOB
                      </span>
                      <p className="text-sm font-medium text-foreground">{formatDOB(selectedPatient.profile?.DOB)}</p>
                    </div>
                    <div>
                      <span className="text-[10px] font-medium text-muted-foreground uppercase">Sex</span>
                      <p className="text-sm font-medium text-foreground capitalize">{selectedPatient.profile?.Sex || '—'}</p>
                    </div>
                    {selectedPatient.profile?.Languages && (
                      <div className="col-span-2">
                        <span className="flex items-center gap-1 text-[10px] font-medium text-muted-foreground uppercase">
                          <Globe className="h-3 w-3" /> Languages
                        </span>
                        <p className="text-sm font-medium text-foreground">
                          {Array.isArray(selectedPatient.profile.Languages) ? selectedPatient.profile.Languages.join(', ') : String(selectedPatient.profile.Languages)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Contact numbers and emails */}
                <div className="space-y-3">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Verified Contacts</h3>

                  {/* Phone list */}
                  <div className="space-y-2">
                    <span className="text-[10px] font-semibold text-muted-foreground uppercase flex items-center gap-1">
                      <Phone className="h-3 w-3" /> Phones
                    </span>
                    {selectedPatient.contacts?.Phones && selectedPatient.contacts.Phones.length > 0 ? (
                      selectedPatient.contacts.Phones.map((p, idx) => (
                        <div key={idx} className="flex items-center justify-between rounded-lg border bg-background px-3 py-2 text-sm shadow-sm">
                          <div className="flex flex-col">
                            <span className="font-mono font-medium">{p.E164}</span>
                            <span className="text-[10px] text-muted-foreground capitalize">{p.Label || 'primary'}</span>
                          </div>
                          <Badge className={cn(
                            "text-[10px] font-medium border-transparent",
                            p.Verified
                              ? "bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300 hover:bg-emerald-500/15"
                              : "bg-secondary text-secondary-foreground"
                          )}>
                            {p.Verified ? 'Verified' : 'Unverified'}
                          </Badge>
                        </div>
                      ))
                    ) : (
                      <div className="rounded-lg border border-dashed p-3 text-center text-xs text-muted-foreground">
                        No phones listed
                      </div>
                    )}
                  </div>

                  {/* Email list */}
                  <div className="space-y-2 pt-2">
                    <span className="text-[10px] font-semibold text-muted-foreground uppercase flex items-center gap-1">
                      <Mail className="h-3 w-3" /> Emails
                    </span>
                    {selectedPatient.contacts?.Emails && selectedPatient.contacts.Emails.length > 0 ? (
                      selectedPatient.contacts.Emails.map((em, idx) => (
                        <div key={idx} className="flex items-center justify-between rounded-lg border bg-background px-3 py-2 text-sm shadow-sm">
                          <div className="flex flex-col">
                            <span className="font-medium truncate max-w-[200px] sm:max-w-[280px]">{em.Email}</span>
                          </div>
                          <Badge className={cn(
                            "text-[10px] font-medium border-transparent",
                            em.Verified
                              ? "bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300 hover:bg-emerald-500/15"
                              : "bg-secondary text-secondary-foreground"
                          )}>
                            {em.Verified ? 'Verified' : 'Unverified'}
                          </Badge>
                        </div>
                      ))
                    ) : (
                      <div className="rounded-lg border border-dashed p-3 text-center text-xs text-muted-foreground">
                        No emails listed
                      </div>
                    )}
                  </div>
                </div>

                {/* Metadata section */}
                <div className="pt-2">
                  <div className="rounded-xl bg-muted/40 p-3 text-[11px] text-muted-foreground flex items-center justify-between border">
                    <span>Patient System ID:</span>
                    <span className="font-mono select-all bg-background px-1.5 py-0.5 rounded border">{selectedPatient.id}</span>
                  </div>
                </div>
                </div>

              </ScrollArea>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SearchPage;
